import type { ErpManifestResponse } from './types.js';

export interface ErpClientOptions {
  /** Base URL of the integration service, e.g. https://part2ful-api.leads-dev.pgdn.app
   *  No trailing slash. */
  baseUrl: string;
  /** Override fetch (tests). */
  fetch?: typeof globalThis.fetch;
}

export class ErpClientHttpError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: string,
  ) {
    super(message);
    this.name = 'ErpClientHttpError';
  }
}

export class ErpClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof globalThis.fetch;

  constructor(opts: ErpClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/+$/, '');
    this.fetchImpl = opts.fetch ?? globalThis.fetch.bind(globalThis);
  }

  async getErpManifest(assemblyId: string, lang?: string): Promise<ErpManifestResponse> {
    const url = new URL(`${this.baseUrl}/api/v1/assemblies/${encodeURIComponent(assemblyId)}/erp-manifest`);
    if (lang) url.searchParams.set('lang', lang);
    const resp = await this.fetchImpl(url.toString(), {
      method: 'GET',
      // credentials:'include' is added in Phase 3 once we have a real JWT cookie.
      // Phase 1 endpoints are unauthenticated, so we send no cookies.
      headers: { Accept: 'application/json' },
    });
    if (!resp.ok) {
      const body = await resp.text().catch(() => '');
      throw new ErpClientHttpError(
        `erp-manifest ${resp.status} ${resp.statusText}`,
        resp.status,
        body,
      );
    }
    return (await resp.json()) as ErpManifestResponse;
  }
}

/** Read the `<meta name="erp-api">` tag and return its content, if present.
 *  When the tag is empty or missing, returns null and the viewer should
 *  fall back to its static `bom-links.json` flow. */
export function readErpApiBaseFromDom(): string | null {
  if (typeof document === 'undefined') return null;
  const meta = document.querySelector<HTMLMetaElement>('meta[name="erp-api"]');
  if (!meta) return null;
  const content = meta.content.trim();
  return content === '' ? null : content;
}
